/**
 * DbEnvToggle (US-AIF.P0-0247-S03 · @UC9419 · @DP-5) — segmented control "Môi trường dữ liệu:
 * Thật | Sandbox" trên topbar admin. CHỈ render cho SUPER_ADMIN/OPS (defence-in-depth UI; backend
 * S01 là chốt thật). Chọn Sandbox ⇒ setDbEnv("sandbox") (đính header + invalidate query + persist);
 * Thật ⇒ setDbEnv("real"). Mirror cách ViewAsUserButton gate theo role.
 */
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { getCurrentAdmin } from "@/lib/auth.js";
import { useOptionalDbEnv } from "./db-env-context.js";

/** Chỉ đặc quyền cao được đổi môi trường dữ liệu (defence-in-depth UI; backend enforce thật). */
const DB_ENV_ROLES = new Set(["SUPER_ADMIN", "OPS"]);

export function DbEnvToggle() {
  const { t } = useTranslation();
  const env = useOptionalDbEnv();
  const current = getCurrentAdmin();
  const canSwitch = current?.role !== undefined && DB_ENV_ROLES.has(current.role);

  // Vắng Provider (harness cô lập) hoặc không đủ quyền → ẩn hoàn toàn (defence-in-depth UX; role
  // lạ/undefined = least privilege). App.tsx production luôn bọc DbEnvProvider nên toggle hiện đúng.
  if (!env || !canSwitch) return null;

  const { dbEnv, setDbEnv } = env;

  return (
    <div
      data-testid="db-env-toggle"
      role="group"
      aria-label={t("dbEnv.label")}
      className="flex h-[34px] shrink-0 items-center gap-1 rounded-sm border border-border bg-muted px-2 text-[12px]"
    >
      {/* Nhãn mô tả ẩn ở màn hẹp (≤lg) để topbar KHÔNG tràn ngang ở 768px (US-AE.F0 /ci no-scroll);
          nút Thật/Sandbox vẫn hiện + group aria-label giữ ngữ cảnh a11y. */}
      <span className="hidden font-medium text-fg-subtle lg:inline">{t("dbEnv.label")}</span>
      <div className="flex items-center rounded-[6px] bg-muted p-0.5">
        <button
          type="button"
          data-testid="db-env-set-real"
          onClick={() => setDbEnv("real")}
          aria-pressed={dbEnv === "real"}
          className={cn(
            "h-7 rounded-[6px] px-2.5 font-semibold",
            dbEnv === "real" ? "bg-surface text-fg shadow" : "text-fg-subtle",
          )}
        >
          {t("dbEnv.real")}
        </button>
        <button
          type="button"
          data-testid="db-env-set-sandbox"
          onClick={() => setDbEnv("sandbox")}
          aria-pressed={dbEnv === "sandbox"}
          className={cn(
            "h-7 rounded-[6px] px-2.5 font-semibold",
            dbEnv === "sandbox"
              ? "bg-tone-warning-fg text-white shadow"
              : "text-fg-subtle",
          )}
        >
          {t("dbEnv.sandbox")}
        </button>
      </div>
    </div>
  );
}
